import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { brand } from "../mock";
import { Coins, ArrowLeft, CalendarDays, Clock, MapPin, LogOut, Sparkles, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const statusStyles = { pending: "bg-amber-100 text-amber-700", confirmed: "bg-blue-100 text-blue-700", assigned: "bg-purple-100 text-purple-700", completed: "bg-green-100 text-green-700", cancelled: "bg-red-100 text-red-600" };

const Account = () => {
  const { user, loading, login, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loadingB, setLoadingB] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try { const res = await axios.get(`${API}/customer/bookings`, { withCredentials: true }); setBookings(res.data); }
      catch {} finally { setLoadingB(false); }
    })();
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/40 px-4">
        <div className="bg-white rounded-2xl shadow-lg ring-1 ring-black/5 p-8 max-w-sm w-full text-center">
          <img src={brand.logo} alt={brand.name} className="h-12 mx-auto object-contain" />
          <h1 className="font-display text-2xl font-bold mt-4 text-gray-900">Welcome Back</h1>
          <p className="text-sm text-gray-500 mt-1">Login to view your bookings & earn loyalty coins</p>
          <Button onClick={login} className="w-full mt-6 bg-primary hover:bg-primary/90 h-11">Continue with Google</Button>
          <button onClick={() => navigate("/")} className="text-sm text-gray-400 mt-4 hover:text-primary">← Back to home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-gray-700 hover:text-primary"><ArrowLeft className="w-4 h-4" /> <img src={brand.logo} alt={brand.name} className="h-9 object-contain" /></button>
          <Button variant="ghost" size="sm" onClick={async () => { await logout(); navigate("/"); }}><LogOut className="w-4 h-4 mr-1.5" /> Logout</Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center gap-4">
          {user.picture ? <img src={user.picture} alt="" className="w-16 h-16 rounded-full ring-2 ring-primary/20" /> : <div className="w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center text-2xl font-bold">{user.name?.[0]}</div>}
          <div><h1 className="font-display text-2xl font-bold text-gray-900">{user.name}</h1><p className="text-gray-500 text-sm">{user.email}</p></div>
        </div>

        <div className="mt-6 bg-gradient-to-br from-primary to-primary/80 rounded-2xl p-6 text-white flex items-center justify-between">
          <div><div className="flex items-center gap-2 text-white/90"><Coins className="w-5 h-5" /> Loyalty Coins</div><div className="text-4xl font-extrabold mt-1">{user.coins}</div><p className="text-white/80 text-sm mt-1">1 coin = ₹1 off · Earn 10 coins per ₹100 spent</p></div>
          <Sparkles className="w-12 h-12 text-white/40" />
        </div>

        <h2 className="font-display text-xl font-bold text-gray-900 mt-8 mb-4">Your Bookings</h2>
        {loadingB ? (
          <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" /></div>
        ) : bookings.length === 0 ? (
          <div className="bg-white rounded-2xl ring-1 ring-black/5 p-12 text-center text-gray-400"><CalendarDays className="w-10 h-10 mx-auto opacity-40" /><p className="mt-3">No bookings yet. Book your first service!</p><Button onClick={() => navigate("/")} className="mt-4 bg-primary hover:bg-primary/90">Browse Services</Button></div>
        ) : (
          <div className="space-y-4">
            {bookings.map((b) => (
              <div key={b.id} className="bg-white rounded-2xl ring-1 ring-black/5 shadow-sm p-5">
                <div className="flex items-start justify-between">
                  <div><div className="font-semibold text-gray-900">{(b.items || []).map((i) => i.name).join(", ")}</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mt-1.5">
                      <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {b.date}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {b.time}</span>
                      <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {b.city}</span>
                    </div>
                    {b.assigned_beautician_name && <div className="text-xs text-purple-600 mt-1">Beautician: {b.assigned_beautician_name}</div>}
                  </div>
                  <Badge className={`capitalize ${statusStyles[b.status]}`}>{b.status}</Badge>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 text-sm">
                  <span className="font-bold text-gray-900">₹{b.total}</span>
                  {b.coins_earned > 0 && <span className={`flex items-center gap-1 ${b.coins_credited ? "text-green-600" : "text-amber-500"}`}><Coins className="w-3.5 h-3.5" /> {b.coins_credited ? `+${b.coins_earned} earned` : `${b.coins_earned} on completion`}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Account;
